import { useRef, useState } from "react";

/**
 * <TeamPhoto3D />
 * Premium 3D-tilt photo card used on the About page Team section.
 *
 * Stack:
 *  - Behind: offset gold matted frame (existing behaviour, slides into alignment on hover)
 *  - Card:   3D-perspective container that tilts toward the cursor on hover (max 4°)
 *  - Image:  rounded, drop-shadow, scale 1.03 on hover
 *  - Inner:  gold inner-glow ring + specular highlight that sweeps across on hover
 *
 * Pure CSS 3D — no Framer Motion / no extra deps.
 */
export default function TeamPhoto3D({ src, alt = "" }) {
  const ref = useRef(null);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [hovered, setHovered] = useState(false);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5; // -0.5 .. 0.5
    const y = (e.clientY - r.top) / r.height - 0.5;
    setTx(-y * 6); // rotateX (negate so tilting up moves top forward)
    setTy(x * 6); // rotateY
  };

  const reset = () => {
    setTx(0);
    setTy(0);
    setHovered(false);
  };

  return (
    <div
      className="relative"
      style={{ perspective: "1100px" }}
      onMouseEnter={() => setHovered(true)}
      onMouseMove={onMove}
      onMouseLeave={reset}
      data-testid="team-photo-3d"
    >
      {/* Offset gold matted frame — sits behind the card */}
      <span
        aria-hidden="true"
        className="absolute inset-0 translate-x-2 translate-y-2 md:translate-x-3 md:translate-y-3 border border-[#C9A050]/70 rounded-md transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-0 group-hover:translate-y-0 group-hover:border-[#C9A050]"
      />

      {/* 3D-tilt card */}
      <div
        ref={ref}
        className="relative aspect-[4/5] overflow-hidden bg-[#121214] rounded-md will-change-transform"
        style={{
          transform: `rotateX(${tx}deg) rotateY(${ty}deg)`,
          transition: hovered
            ? "transform 80ms ease-out, box-shadow 600ms ease"
            : "transform 700ms cubic-bezier(0.22,1,0.36,1), box-shadow 600ms ease",
          boxShadow: hovered
            ? "0 28px 64px -18px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,160,80,0.35), 0 0 32px -6px rgba(201,160,80,0.25)"
            : "0 18px 48px -12px rgba(0,0,0,0.55)",
          transformStyle: "preserve-3d",
        }}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            transform: hovered ? "scale(1.04) translateZ(20px)" : "scale(1) translateZ(0)",
          }}
        />

        {/* Specular gold highlight — sweeps diagonally on hover */}
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(115deg, transparent 30%, rgba(201,160,80,0.18) 48%, rgba(255,255,255,0.06) 52%, transparent 70%)",
            transform: hovered ? "translateX(0%)" : "translateX(-110%)",
            transition: "transform 1200ms cubic-bezier(0.22,1,0.36,1)",
            mixBlendMode: "screen",
          }}
        />

        {/* Inner gold edge ring */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-md pointer-events-none"
          style={{
            boxShadow: "inset 0 0 0 1px rgba(201,160,80,0.18)",
            opacity: hovered ? 1 : 0.5,
            transition: "opacity 600ms ease",
          }}
        />
      </div>
    </div>
  );
}
